import _ from 'lodash'
import KFocused from '../../core/mixins/focused/KFocused'
import KControl from '../../core/mixins/control/KControl'
import { convertPropClass2ClassObjectLike } from '../../core/utils/KProps'
import KEmitter from '../../core/mixins/emitter/KEmitter'
import Icon from '../icon'
import Loading from '../loading'
import ScrollBar from '../scrollbar'

export default {
  name: 'KInput',
  mixins: [KFocused, KControl, KEmitter],
  props: {
    color: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
    value: {
      validator() {
        return true
      },
      default: null,
      // type: String,
      // default: '',
    },
    lazy: {
      type: Boolean,
      default: false,
    },
    trim: {
      type: Boolean,
      default: false,
    },
    number: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    static: {
      type: Boolean,
      default: false,
    },
    size: {
      validator: (value) => ['large', 'normal', 'small', 'mini'].indexOf(value) !== -1,
      default: 'normal',
    },
    prefix: {
      // 前缀图标
      type: String,
      default: '',
    },
    suffix: {
      // 后缀图标
      type: String,
      default: '',
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    clearSuffix: {
      type: Boolean,
      default: false,
    },
    clearableOnHover: {
      type: Boolean,
      default: false,
    },
    clearAnim: {
      type: Boolean,
      default: false,
    },
    inputClass: {
      validator: (value) => _.isString(value) || _.isArray(value) || _.isObjectLike(value),
      // {}
      default() {
        return {}
      },
    },
    originalReadonly: {
      // 仅原生 input 置为 readonly，用在需要 input 表现为非 readonly，且不可让用户主动修改，需要通过其他动作来改变值得情况（例如 select 组件）
      type: Boolean,
      default: false,
    },
    rounded: {
      type: Boolean,
    },
    spellCheck: {
      type: Boolean,
      default: true,
    },
    // ignore [IME](https://developer.mozilla.org/en-US/docs/Mozilla/IME_handling_guide) trigger change
    // reference:
    // https://developer.mozilla.org/en-US/docs/Web/API/CompositionEvent
    // https://github.com/facebook/react/issues/3926
    // ignoreIme: {
    //   type: Boolean,
    //   default: false,
    // },
  },
  data() {
    return {
      hovered: false,
      focused: false,
      innerValue: '',
      isTextArea: false,
    }
  },
  computed: {
    hasIconPrefix() {
      return this.$slots.prefix || this.prefix
    },
    hasIconSuffix() {
      return this.$slots.suffix || this.suffix
    },
    shouldRenderClear() {
      return this.clearable && this.innerValue && (this.clearableOnHover ? this.hovered : true)
    },
    inputClassObjectLike() {
      return convertPropClass2ClassObjectLike(this.inputClass)
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(value) {
        this.innerValue = value
      },
    },
    innerValue(value) {
      this.$emit('input', value)
      this.$emit('change', value)
      this.dispatch('KFormItem', 'k.form.change', [value])
    },
    focused(value) {
      this.$emit(value ? 'focus' : 'blur')
    },
  },
  created() {
  },
  mounted() {
  },
  methods: {
    renderLoadingIcon() {
      if (!this.loading) {
        return null
      }

      return (
        <div class="km-input__loading">
          <Loading
            size={this.size}
            name="spinner"
            class="km-input__loading-icon"
          />
        </div>
      )
    },
    renderClearButton() {
      const css = {
        'km-input__icon': true,
        'km-input__icon--action': true,
        'km-input__clear': true,
        'km-input__clear--suffix': this.clearSuffix,
      }

      return this.clearAnim ? (
        <transition name="km-input-clear">
          {this.shouldRenderClear ? (
            <a
              class={css}
              onClick={this.handleClear}
            >
              <Icon icon="times-circle-s" />
            </a>
          ) : null}
        </transition>
      ) : (
        this.shouldRenderClear ? (
          <a
            class={css}
            onClick={this.handleClear}
          >
            <Icon icon="times-circle-s" />
          </a>
        ) : null
      )
    },
    renderIcon(slotName, icon) {
      const css = {
        'km-input__icon': true,
        'km-input__icon--clear': slotName === 'suffix' && this.shouldRenderClear && this.clearSuffix,
      }

      return (
        <div class={css}>
          {this.$slots[slotName] || <Icon icon={icon} />}
          {slotName === 'suffix' && this.clearSuffix ? this.renderClearButton() : null}
        </div>
      )
    },
    renderPrefix() {
      return this.hasIconPrefix ? this.renderIcon('prefix', this.prefix) : null
    },
    renderSuffix() {
      return this.hasIconSuffix ? this.renderIcon('suffix', this.suffix) : null
    },
    renderCustom(data) {
      // if (this.$slots.default && !_.isNil(this.value)) {
      if (this.$slots.default) {
        data = _.cloneDeep(data)
        delete data.style['display']

        return <div
          ref="custom"
          class={{
            'km-input__custom': true,
            ...data.class,
          }}
        >
          {this.$slots.default}
        </div>
      }
    },
    renderOriginal() {
      const inputCss = {
        'km-input__original': true,
        [`is-${this.size}`]: true,
        'is-textarea': this.isTextArea,
        'is-autoresize': this.innerAutoSize,
        ...this.inputClassObjectLike,
      }

      const style = {}
      if (this.isTextArea) {
        if (!this.resizable) {
          style.resize = 'none'
        }
        // if (this.height) {
        //   style.height = this.height
        // }
      }

      if (this.$slots.default && !_.isNil(this.value)) {
        style.display = 'none'
      }

      const readonly = this.readonly || this.originalReadonly
      const disabled = this.disabled
      const attrs = {
        ...this.$attrs,
        readonly,
        disabled,
        tabindex: readonly || disabled ? -1 : 1,
        spellcheck: this.spellCheck,
      }

      const listeners = _.reduce(this.$listeners, (result, event, eventName) => {
        if (['input'].indexOf(eventName) === -1) {
          result[eventName] = event
        }
        return result
      }, {})

      const events = {
        ...listeners,
        // input: this.handleChange,
        // change: (e) => {
        //   this.handleChange(e, true)
        // },
        focus: this.handleFocus,
        blur: this.handleBlur,
      }

      const data = {
        class: inputCss,
        style,
        attrs,
        on: events,
      }

      const loadingIcon = this.renderLoadingIcon()

      const custom = this.renderCustom(data)

      // const inputTag = this.isTextArea ? 'textarea' : 'input'
      if (this.isTextArea) {
        if (custom) {
          return <div
            ref="originalWrapper"
            class="km-input__original-wrapper"
          >
            {custom}
            {loadingIcon}
          </div>
        }

        return <ScrollBar
          ref="originalWrapper"
          class="km-input__original-wrapper"
        >
          <textarea
            {...data}
            vModel={this.innerValue}
            ref="original"
          />
        </ScrollBar>
      }

      return <div
        ref="originalWrapper"
        class="km-input__original-wrapper"
      >
        {
          custom ||
          <input
            {...data}
            vModel={this.innerValue}
            ref="original"
          />
        }
        {loadingIcon}
      </div>
    },
    handleClear(event) {
      this.innerValue = ''
      this.$emit('clear', event)
    },
    handleControlClick(target, isClickOutside) {
      if (this.disabled || this.readonly) {
        return
      }

      if (isClickOutside) {
        this.focused = false
        return
      }

      this.focused = true
      this.$nextTick(() => {
        this.$refs.original && this.$refs.original.focus()
      })
    },
    handleControlActive() {
      this.$nextTick(() => {
        this.focused = true
      })
    },
    handleChange(event, fromChange = false) {
      const value = event.target.value
      if (!this.lazy) {
        this.innerValue = value
      } else if (fromChange) {
        this.innerValue = value
      }
    },
    handleFocus() {
      this.focused = true
    },
    handleBlur() {
      this.focused = false
      this.dispatch('KFormItem', 'k.form.blur', [this.value])
    },
    // input method:
    focus() {
      this.$refs.original && this.$refs.original.focus()
    },
    blur() {
      this.$refs.original && this.$refs.original.blur()
    },
    select() {
      this.$refs.original && this.$refs.original.select()
    },
  },
  render() {
    const controlCss = {
      'is-disabled': this.disabled,
      'is-focused': this.focused,
      'is-readonly': this.readonly,
      'is-static': this.static,
      [`is-${this.size}`]: true,
      'has-icon-prefix': this.hasIconPrefix,
      'has-icon-suffix': this.hasIconSuffix || this.shouldRenderClear,
      'is-textarea': this.isTextArea,
      'is-height-fixed': this.isTextArea && this.height,
      'is-rounded': this.rounded,
    }
    if (this.color) {
      controlCss[`km-input--${this.color}`] = true
    }

    const style = {}
    if (this.isTextArea && this.height) {
      style['height'] = this.height
    }

    const directives = [
      {
        name: 'click-outside',
        value: this.handleControlClick,
        modifiers: { both: true },
      },
    ]

    const events = {
      mousedown: this.handleControlActive,
      mouseenter: () => {
        this.hovered = true
      },
      mouseleave: () => {
        this.hovered = false
      },
    }

    const prefix = this.renderPrefix()
    const suffix = this.renderSuffix()
    const original = this.renderOriginal()

    return (
      <div
        class="km-control km-input"
        {...{ style: style }}
        {...{ class: controlCss }}
        {...{ on: events }}
        {...{ directives }}>
        {prefix}
        {original}
        {this.clearSuffix ? null : this.renderClearButton()}
        {suffix}
      </div>
    )
  },
}
